import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Search,
  Target,
  Users,
  CheckCircle,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  FileText,
  UserCheck,
  Briefcase,
  MessageSquare,
  Sparkles,
  Twitter,
  Linkedin,
  Youtube,
  MessageCircle,
  Award,
  Building,
  ClipboardList,
  UserPlus,
} from 'lucide-react';
import { useThemeStore } from '../../../store/theme';

// CSS Keyframe animations
const keyframeStyles = `
  @keyframes float-gentle {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(3deg); }
  }

  @keyframes pulse-teal {
    0%, 100% { box-shadow: 0 0 20px rgba(20, 184, 166, 0.4); }
    50% { box-shadow: 0 0 40px rgba(20, 184, 166, 0.7); }
  }

  @keyframes orbit-recruit {
    from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
  }

  @keyframes orbit-recruit-reverse {
    from { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
    to { transform: rotate(0deg) translateX(100px) rotate(0deg); }
  }

  @keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes ripple {
    0% { transform: scale(0.8); opacity: 1; }
    100% { transform: scale(2); opacity: 0; }
  }

  @keyframes bounce-soft {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }

  @keyframes search-float {
    0%, 100% { transform: translateY(0) rotate(-5deg); opacity: 0.6; }
    50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
  }

  .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
  .animate-pulse-teal { animation: pulse-teal 3s ease-in-out infinite; }
  .animate-orbit-recruit { animation: orbit-recruit 20s linear infinite; }
  .animate-orbit-recruit-reverse { animation: orbit-recruit-reverse 15s linear infinite; }
  .animate-gradient-flow { animation: gradient-flow 4s ease infinite; background-size: 200% 200%; }
  .animate-ripple { animation: ripple 2s ease-out infinite; }
  .animate-bounce-soft { animation: bounce-soft 3s ease-in-out infinite; }
  .animate-search-float { animation: search-float 4s ease-in-out infinite; }
`;

// Helper function to render markdown-style bold text
const renderMessageContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Chat demo messages
const chatMessages = [
  { role: 'user', message: "We need to hire a senior full-stack developer, but we've been struggling to find qualified candidates. Can you help?" },
  { role: 'assistant', message: "Absolutely! Finding senior developers is competitive, but very doable with the right approach. Let me help you create a strategy. First, a few quick questions: 1) What's your tech stack? 2) Remote, hybrid, or on-site? 3) What's your salary range? 4) Any must-have skills vs nice-to-haves? This will help me craft a job description that attracts the right candidates and stands out from generic postings." },
  { role: 'user', message: "React/Node stack, fully remote, $150-180k range. Must have 5+ years experience and API design skills. Nice to have: AWS experience." },
  { role: 'assistant', message: "Great specs! Here's my strategy: I'll create a compelling job description that leads with your remote flexibility and competitive salary - these are your biggest selling points. For sourcing, I recommend: 1) LinkedIn targeted outreach to passive candidates, 2) Developer communities like Dev.to and relevant Discord servers, 3) Your team's networks with a referral bonus. I'll also prepare screening questions focused on system design and real-world problem-solving rather than just trivia. Want me to draft the job description first, or start with the interview question framework?" },
];

// Testimonials
const testimonials = [
  {
    name: 'Rachel Kim',
    role: 'Head of People',
    company: 'ScaleUp Inc',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face',
    quote: 'Scout cut our time-to-hire by 60%. The job descriptions it creates actually attract qualified candidates, and the screening questions are spot-on for identifying top talent.',
    rating: 5,
  },
  {
    name: 'James Wilson',
    role: 'Founder',
    company: 'TechStart',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    quote: "As a founder wearing multiple hats, Scout has been invaluable. It helped me build my first 10 hires with job posts that attracted exactly the culture fit we needed. Game changer.",
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    role: 'HR Director',
    company: 'Enterprise Corp',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
    quote: 'The onboarding plans Scout creates are incredibly thorough. New hires feel welcomed and productive from day one. Our 90-day retention improved by 40%.',
    rating: 5,
  },
];

// Capabilities with unique images
const capabilities = [
  {
    icon: FileText,
    title: 'Job Description Writing',
    description: 'Create compelling job postings that attract qualified candidates and clearly communicate role expectations, culture, and growth opportunities.',
    image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=600&h=400&fit=crop',
  },
  {
    icon: Search,
    title: 'Candidate Sourcing',
    description: 'Develop multi-channel sourcing strategies to find both active job seekers and passive candidates who match your ideal profile.',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=400&fit=crop',
  },
  {
    icon: ClipboardList,
    title: 'Resume Screening',
    description: 'Create efficient screening criteria and processes to quickly identify top candidates without missing hidden gems.',
    image: 'https://images.unsplash.com/photo-1586473219010-2ffc57b0d282?w=600&h=400&fit=crop',
  },
  {
    icon: MessageSquare,
    title: 'Interview Questions',
    description: 'Design role-specific interview questions that reveal true capabilities, problem-solving skills, and cultural alignment.',
    image: 'https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=600&h=400&fit=crop',
  },
  {
    icon: UserPlus,
    title: 'Onboarding Plans',
    description: 'Build comprehensive onboarding programs that help new hires feel welcomed, get productive quickly, and stay long-term.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
  },
  {
    icon: Building,
    title: 'Employer Branding',
    description: "Develop your company's reputation as an employer of choice with authentic messaging that resonates with your target talent.",
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop',
  },
];

// Discover more tasks
const discoverTasks = [
  {
    title: 'Write a Job Description',
    description: "Skip the boring templates. Scout creates job posts that actually stand out - highlighting what makes your role and company special so you attract candidates who are genuinely excited to apply.",
    gif: 'https://media.giphy.com/media/3oKIPtjElfqwMOTbH2/giphy.gif',
    prompt: 'Help me write a compelling job description that attracts top talent',
  },
  {
    title: 'Create Interview Questions',
    description: "Stop asking the same tired questions everyone Googles answers for. Get questions that reveal how candidates actually think, solve problems, and would perform in your specific role.",
    gif: 'https://media.giphy.com/media/l46Cy1rHbQ92uuLXa/giphy.gif',
    prompt: 'Help me prepare interview questions that reveal candidate capabilities',
  },
  {
    title: 'Build Onboarding Plan',
    description: "First impressions matter. Create a structured onboarding experience that helps new hires feel welcomed, understand expectations, and become productive without that overwhelming 'drinking from a firehose' feeling.",
    gif: 'https://media.giphy.com/media/3oKIPEqDGUULpEU0aQ/giphy.gif',
    prompt: 'Help me create an onboarding plan for new employees',
  },
  {
    title: 'Develop Sourcing Strategy',
    description: "The best candidates aren't always actively looking. Scout helps you find where your ideal candidates hang out and craft outreach that gets responses instead of silence.",
    gif: 'https://media.giphy.com/media/LmNwrBhejkK9EFP504/giphy.gif',
    prompt: 'Help me develop a candidate sourcing strategy for hard-to-fill roles',
  },
  {
    title: 'Screen Resumes Effectively',
    description: "Drowning in applications? Create clear screening criteria that help you quickly spot the best candidates while avoiding unconscious bias that might cause you to miss great fits.",
    gif: 'https://media.giphy.com/media/xT9C25UNTwfZuk85WP/giphy.gif',
    prompt: 'Help me create a resume screening process for evaluating candidates',
  },
  {
    title: 'Improve Employer Brand',
    description: "Great candidates research companies before applying. Build authentic employer branding that showcases your culture, values, and what makes working at your company genuinely special.",
    gif: 'https://media.giphy.com/media/3oKIPnAiaMCws8nOsE/giphy.gif',
    prompt: 'Help me improve my employer branding to attract better candidates',
  },
];

// Related employees
const relatedEmployees = [
  { id: 'sage', name: 'Sage', role: 'Business Strategist', avatar: '🧠', gradient: 'from-emerald-500 to-green-500' },
  { id: 'ink', name: 'Ink', role: 'Copywriter', avatar: '✍️', gradient: 'from-indigo-500 to-violet-500' },
  { id: 'agent', name: 'Agent', role: 'Conversational Agent', avatar: '🤖', gradient: 'from-sky-500 to-blue-500' },
];

// Stats
const stats = [
  { label: 'Hires Facilitated', value: '25K+', icon: UserCheck },
  { label: 'User Satisfaction', value: '4.9/5', icon: Star },
  { label: 'Time-to-Hire Reduction', value: '60%', icon: Zap },
  { label: 'Roles Supported', value: '500+', icon: Briefcase },
];

export default function ScoutEmployee() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chatRef, { once: true, margin: '-100px' });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate chat messages when in view
  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => prev + 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, visibleMessages]);

  // JSON-LD Schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Scout - AI Recruiter',
    applicationCategory: 'BusinessApplication',
    description: 'AI-powered recruiter that helps find, screen, and onboard top talent that fits your culture.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '1892',
    },
    operatingSystem: 'Web-based',
    featureList: [
      'Job description writing',
      'Candidate sourcing',
      'Resume screening',
      'Interview questions',
      'Onboarding plans',
      'Employer branding',
      'Salary benchmarking',
      'Offer letter drafting',
    ],
  };

  return (
    <>
      <Helmet>
        <title>Scout - AI Recruiter | SweetPilot</title>
        <meta
          name="description"
          content="Meet Scout, your AI recruiter. Expert at finding, screening, and onboarding top talent that fits your culture and drives your company forward."
        />
        <meta
          name="keywords"
          content="AI recruiter, hiring assistant, job description writer, interview questions, candidate sourcing, onboarding, employer branding, talent acquisition"
        />
        <link rel="canonical" href="https://sweetpilot.com/employees/scout" />

        {/* Open Graph */}
        <meta property="og:title" content="Scout - AI Recruiter | SweetPilot" />
        <meta
          property="og:description"
          content="Your AI recruiter for finding, screening, and onboarding top talent. Build your dream team faster."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sweetpilot.com/employees/scout" />
        <meta property="og:image" content="https://sweetpilot.com/og-scout.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scout - AI Recruiter" />
        <meta
          name="twitter:description"
          content="Your AI recruiter for finding, screening, and onboarding top talent. Build your dream team faster."
        />
        <meta name="twitter:image" content="https://sweetpilot.com/og-scout.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{keyframeStyles}</style>

      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Floating Search Icons Background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-search-float"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${4 + i * 0.5}s`,
              }}
            >
              <Search
                className={`w-6 h-6 ${isDark ? 'text-teal-500/20' : 'text-teal-400/30'}`}
              />
            </div>
          ))}
        </div>

        {/* Navigation Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-dark-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/employees" className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Employees</span>
              </Link>
              <Link
                to="/dashboard/employees/scout"
                className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-teal-500/25 transition-all"
              >
                Chat with Scout
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-cyan-500/10" />

          {/* Animated ripples */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-96 h-96 rounded-full border-2 border-teal-500/20 animate-ripple" />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-teal-500/20 animate-ripple" style={{ animationDelay: '0.5s' }} />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-teal-500/20 animate-ripple" style={{ animationDelay: '1s' }} />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/employees"
                    className={`transition-colors ${isDark ? 'hover:text-teal-400' : 'hover:text-teal-600'}`}
                  >
                    AI Employees
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Scout
                </li>
              </ol>
            </nav>

            <div className="text-center">
              {/* Avatar with orbiting icons */}
              <div className="relative w-48 h-48 mx-auto mb-8">
                {/* Orbiting icons */}
                <div className="absolute inset-0 animate-orbit-recruit">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                      <Users className="w-6 h-6 text-teal-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-recruit-reverse">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                      <FileText className="w-6 h-6 text-cyan-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-recruit" style={{ animationDelay: '-10s' }}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <UserCheck className="w-6 h-6 text-emerald-500" />
                    </div>
                  </div>
                </div>

                {/* Main avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center animate-float-gentle animate-pulse-teal">
                    <span className="text-7xl">🔍</span>
                  </div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className={`text-5xl md:text-7xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Meet{' '}
                  <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    Scout
                  </span>
                </h1>
                <p className={`text-xl md:text-2xl mb-2 ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                  Your AI Recruiter
                </p>
                <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Expert at finding, screening, and onboarding top talent that fits your culture - helping you build your dream team faster and smarter.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                <Link
                  to="/dashboard/employees/scout"
                  className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-teal-500/30 transition-all flex items-center gap-2"
                >
                  Start Hiring Smarter
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#capabilities"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-teal-500/50 text-teal-400 hover:bg-teal-500/10'
                      : 'border-teal-500 text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  Explore Features
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
                  >
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-teal-400' : 'text-teal-500'}`} />
                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section ref={chatRef} className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                See Scout in Action
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Watch how Scout helps you find and hire top talent
              </p>
            </motion.div>

            {/* Chat Window */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-900 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-lg">🔍</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Scout</div>
                    <div className="flex items-center gap-1 text-sm text-teal-500">
                      <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" />
                      Online - Talent Acquisition
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 min-h-[400px]">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                            : isDark
                            ? 'bg-dark-700 text-gray-200'
                            : 'bg-white text-slate-700 shadow-sm'
                        }`}
                      >
                        <span className="whitespace-pre-line">{renderMessageContent(msg.message)}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isInView && visibleMessages < chatMessages.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-700' : 'bg-white shadow-sm'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Everything You Need for{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Talent Acquisition
                </span>
              </h2>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Scout comes equipped with powerful capabilities to help you build your dream team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden border ${
                    isDark ? 'bg-dark-800/50 border-white/5 hover:border-teal-500/30' : 'bg-white border-slate-200 hover:border-teal-300'
                  } transition-all hover:shadow-xl hover:shadow-teal-500/10`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500">
                        <capability.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {capability.title}
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                      {capability.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Hiring Areas Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Support Across the Entire Hiring Journey
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Scout helps at every stage from sourcing to onboarding
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: FileText, label: 'Job Postings', color: 'from-teal-500 to-cyan-500' },
                { icon: Search, label: 'Sourcing', color: 'from-cyan-500 to-blue-500' },
                { icon: ClipboardList, label: 'Screening', color: 'from-blue-500 to-indigo-500' },
                { icon: MessageSquare, label: 'Interviewing', color: 'from-indigo-500 to-purple-500' },
                { icon: UserPlus, label: 'Onboarding', color: 'from-purple-500 to-teal-500' },
              ].map((area, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl ${
                    isDark ? 'bg-dark-900/50' : 'bg-slate-50'
                  }`}
                >
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${area.color}`}>
                    <area.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {area.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Discover More Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Discover What Scout Can Do
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Click any task to try it with Scout
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverTasks.map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/dashboard/employees/scout?prompt=${encodeURIComponent(task.prompt)}`}
                    className={`group relative block min-h-72 rounded-2xl overflow-hidden border ${
                      isDark ? 'border-white/10 hover:border-teal-500/50' : 'border-slate-200 hover:border-teal-300'
                    } transition-all`}
                  >
                    {/* Background Image/GIF */}
                    <div className="absolute inset-0">
                      <img
                        src={task.gif}
                        alt={task.title}
                        className="w-full h-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 ${isDark ? 'bg-dark-800' : 'bg-white'} group-hover:opacity-80 transition-opacity`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="mb-auto">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Search className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {task.title}
                      </h3>
                      <p className={`line-clamp-3 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className={`mt-4 flex items-center gap-2 text-teal-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span>Try this now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Trusted by HR Leaders
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                See how Scout is transforming talent acquisition
              </p>
            </motion.div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 justify-center">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className={`text-xl text-center mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    "{testimonials[currentTestimonial].quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? 'w-8 bg-gradient-to-r from-teal-500 to-cyan-500'
                        : isDark
                        ? 'bg-white/20 hover:bg-white/40'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Employees */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Meet More AI Employees
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Build your complete AI team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/employees/${employee.id}`}
                    className={`group block p-6 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{employee.avatar}</span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}
                    </h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {employee.role}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-teal-500/10 via-dark-900 to-cyan-500/10' : 'bg-gradient-to-br from-teal-50 via-white to-cyan-50'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center animate-float-gentle">
                <span className="text-5xl">🔍</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to Build Your Dream Team?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Start hiring smarter today with Scout as your recruiting partner
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/employees/scout"
                  className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-teal-500/30 transition-all flex items-center gap-2"
                >
                  Chat with Scout Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/employees"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  View All Employees
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
              {/* Logo */}
              <div className="col-span-2">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                    alt="SweetPilot"
                    className="w-48 object-contain"
                  />
                </Link>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  AI employees that never sleep. Build, grow, and scale your
                  business 24/7.
                </p>
                <div className="flex space-x-3">
                  {[
                    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/sweetpilot' },
                    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/sweetpilot' },
                    { name: 'Discord', icon: MessageCircle, href: 'https://discord.gg/sweetpilot' },
                    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@sweetpilot' },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark
                          ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                          : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      <span className="sr-only">{social.name}</span>
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Links */}
              {[
                {
                  title: 'Product',
                  links: [
                    { name: 'AI Employees', href: '/employees' },
                    { name: 'Features', href: '/features' },
                    { name: 'Pricing', href: '/pricing' },
                    { name: 'Integrations', href: '/integrations' },
                    { name: 'API', href: '/api' },
                  ],
                },
                {
                  title: 'Company',
                  links: [
                    { name: 'About', href: '/about' },
                    { name: 'Blog', href: '/blog' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Press', href: '/press' },
                    { name: 'Contact', href: '/contact' },
                  ],
                },
                {
                  title: 'Resources',
                  links: [
                    { name: 'Documentation', href: '/docs' },
                    { name: 'Help Center', href: '/help' },
                    { name: 'Community', href: '/community' },
                    { name: 'Templates', href: '/templates' },
                    { name: 'Roadmap', href: '/roadmap' },
                    { name: 'Status', href: '/status' },
                  ],
                },
                {
                  title: 'Legal',
                  links: [
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' },
                    { name: 'Security', href: '/security' },
                    { name: 'Cookies', href: '/cookies' },
                    { name: 'GDPR', href: '/gdpr' },
                    { name: 'Refund Policy', href: '/refund-policy' },
                  ],
                },
              ].map((column) => (
                <div key={column.title}>
                  <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                  <ul className="space-y-3">
                    {column.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
              <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                &copy; {new Date().getFullYear()} SweetPilot. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Terms of Service
                </Link>
                <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
